/**
 * Checks if value is a plain object (created by the Object constructor or one with a [[Prototype]] of null).
 */
function isPlainObject(value: any): boolean {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/**
 * Checks if value is an Array.
 */
function isArray(value: any): value is any[] {
  return Array.isArray(value)
}

/**
 * Checks if value can be merged (is an object or array).
 */
function isMergeable(value: any): boolean {
  return isPlainObject(value) || isArray(value)
}

/**
 * Deeply merges source objects into the destination object and returns the destination object.
 * Properties in the destination object will be overwritten by properties in the sources if they
 * have the same key. If you have nested objects, they're recursively merged.
 * Array elements are merged by position.
 *
 * @param object The destination object.
 * @param sources The source objects.
 * @returns The destination object.
 */
export function merge<T extends object, S extends any[]>(
  object: T,
  ...sources: S
): T & S[number] {
  // Return the object if no sources provided
  if (!sources.length) return object as T & S[number]

  const source = sources.shift()

  // Return the object if source is undefined or null
  if (source === undefined || source === null) return merge(object, ...sources)

  // Skip non-object sources but continue with remaining sources
  if (!isMergeable(source)) return merge(object, ...sources)

  // Skip if object is not mergeable (shouldn't happen with typescript, but just in case)
  if (!isMergeable(object)) return merge(object, ...sources)

  // Both are mergeable, proceed with merge
  Object.keys(source).forEach(key => {
    const srcValue = (source as any)[key]
    const targetValue = (object as any)[key]

    // If both values are mergeable objects (plain objects or arrays), merge them recursively
    if (isMergeable(targetValue) && isMergeable(srcValue)) {
      (object as any)[key] = merge(targetValue, srcValue)
    }
    // If target doesn't exist or source is not mergeable, just assign the source value
    else if (srcValue !== undefined) {
      // Clone arrays and objects when assigning
      if (isArray(srcValue)) {
        (object as any)[key] = [...srcValue]
      } else if (isPlainObject(srcValue)) {
        (object as any)[key] = { ...srcValue }
      } else {
        (object as any)[key] = srcValue
      }
    }
  })

  return merge(object, ...sources)
}

export default merge